import IdList from "../data/id-list";
import WrapperService from "@/modules/core/services/wrapper-service";
import Utils from "./utils";
import {createPageableParams} from "@/modules/core/services/table-utils";
import {DocumentType} from '@/modules/book/data/document-type.js';

const sortFacilityDocumentationFields = ['translatedType'];
const sortFacilityDocumentationDatabaseFields = ['fd.type'];

const sortUserDocumentationFields = ['roleName'];
const sortUserDocumentationDatabaseFields = ['w.name'];

const API_URL = process.env.VUE_APP_ROOT_API + '/api/v1/documentation'
const API_STATIC_URL = process.env.VUE_APP_ROOT_STATIC + '/documentation'

const serviceName = 'Documentation-Service: ';

export default class DocumentationService {

  static download(filename, data) {
    DocumentationService.downloadBlob(filename, window.URL.createObjectURL(new Blob([data])));
  }
  static downloadBlob(filename, data) {
    const link = document.createElement('a');
    link.href = data;
    link.setAttribute('download', filename); //or any other extension
    document.body.appendChild(link);
    link.click();
  }

  static async downloadFile(id, filename, type, ownerId, documentType) {
    let url = '';

    if (ownerId && documentType) {
      url = DocumentationService.getStaticFileLinkByDocType(id, type, documentType, ownerId);
    } else {
      url = API_STATIC_URL + "/" + id + "." + Utils.getFileExtensionFromMIME(type);
    }

    await WrapperService.get(url, {responseType: 'blob'}, serviceName,
        (resp) => {
          this.download(filename, resp.data);
        }
    )
  }

  static getStaticFileLink(id, type) {
    var ext = Utils.getFileExtensionFromMIME(type);
    return API_STATIC_URL + "/" + id + "." + ext;
  }

  static getStaticFileLinkByDocType(id, type, documentType, ownerId) {
    let ext = Utils.getFileExtensionFromMIME(type);
    let path = '';

    switch (documentType) {
      case DocumentType.FACILITY:
        path = `/facility/${ownerId}/docs/${id}.${ext}`;
        break;
      case DocumentType.STEP:
        path = `/facility/${ownerId}/steps/${id}.${ext}`;
        break;
      case DocumentType.USER:
        path = `/user/${ownerId}/${id}.${ext}`;
        break;
      case DocumentType.GLOBAL:
        path = `/global/${ownerId}/${id}.${ext}`;
        break;
      case DocumentType.INVOICE_TASK:
      case DocumentType.TASK:
      case DocumentType.OFFER_TASK:
        path = `/task/${ownerId}/${id}.${ext}`;
    }

    return API_STATIC_URL + path;
  }

  static async getFileAsDataUrl(id, type, onSuccess, errorF) {
    let url = DocumentationService.getStaticFileLink(id, type);
    await WrapperService.get(url, {responseType: 'blob'}, serviceName,
        response => {
          let reader = new window.FileReader()
          reader.readAsDataURL(response.data)
          reader.onload = () => {
            onSuccess(reader.result)
          }
        }, errorF
    )
  }

  static async getDocByTypeAndId(id, type, documentType, ownerId, onSuccess, errorF, cleanCache = false) {
    let url = DocumentationService.getStaticFileLinkByDocType(id, type, documentType, ownerId);

    if (cleanCache) {
      url += '?cache=' + Date.now();
    }

    await WrapperService.get(url, {responseType: 'blob'}, serviceName,
      response => {
        let reader = new window.FileReader()
        reader.readAsDataURL(response.data)
        reader.onload = () => {
          onSuccess(reader.result, response.data)
        }
      }, errorF
    )
  }

  static async downloadFileForPublic(id, filename, type, documentType, ownerId, qrCode) {
    let url = '';

    if (ownerId && documentType) {
      url = DocumentationService.getStaticFileLinkByDocType(id, type, documentType, ownerId);
    } else {
      url = API_STATIC_URL + '/' + id + "." + Utils.getFileExtensionFromMIME(type);
    }

    await WrapperService.get(url + '?' + qrCode, {responseType: 'blob'}, serviceName,
        (resp) => {
          this.download(filename, resp.data);
        }
      )
  }

  static async getDocByTypeAndIdForPublic(id, type, documentType, ownerId, qrCode, onSuccess, errorF) {
    let url = DocumentationService.getStaticFileLinkByDocType(id, type, documentType, ownerId);

    await WrapperService.get(url + '?' + qrCode, {responseType: 'blob'}, serviceName,
        response => {
          let reader = new window.FileReader()
          reader.readAsDataURL(response.data)
          reader.onload = () => {
            onSuccess(reader.result)
          }
        }, errorF
      )
  }

  static async getFileAsDataUrlForPublic(id, type, qrCode, onSuccess, errorF) {
    let url = DocumentationService.getStaticFileLink(id, type);
    await WrapperService.get(url + '?' + qrCode, {responseType: 'blob'}, serviceName,
        response => {
          let reader = new window.FileReader()
          reader.readAsDataURL(response.data)
          reader.onload = () => {
            onSuccess(reader.result)
          }
        }, errorF
    )
  }

  static async readInfoFromApi(id, successF, errorF) {
    await WrapperService.get(API_URL + '/' + id + '/info', null, serviceName, successF, errorF);
  }

  static async readInfoListFromApi(idList, successF, errorF) {
    let data = new IdList();
    data.ids = idList;
    await WrapperService.post(API_URL + '/info', data, serviceName, successF, errorF);
  }

  static postFileToAPI(file, documentation, type, ownerId, successF, errorF) {
    let formData = new FormData();
    // files
    formData.append('media', file, file.name);
    // additional data
    formData.append('name', documentation.name)
    formData.append('start', documentation.startDate);
    formData.append('end', documentation.endDate);
    formData.append('ownerId', ownerId);

    WrapperService.post(API_URL + '/' + type, formData, serviceName, successF, errorF);
  }

  static async readPageableUserDocumentationsFromAPI(page, size, search, sort, sortAsc, successF, errorF) {
    let params = createPageableParams(page, size, search, sort, sortAsc, sortUserDocumentationFields, sortUserDocumentationDatabaseFields);
    await WrapperService.get(API_URL + '/profile/user', {params: params}, 'Documentation-Service: ', successF, errorF);
  }

  static async readPageableDocumentationsFromAPI(page, size, search, sort, sortAsc, showAll, successF, errorF) {
    let params = createPageableParams(page, size, search, sort, sortAsc, sortUserDocumentationFields, sortUserDocumentationDatabaseFields);
    params.append('showAll', showAll)
    await WrapperService.get(API_URL + '/documentations', {params: params}, 'Documentation-Service: ', successF, errorF);
  }

  static postUserDocumentationToAPI(file, userDocumentation, successF, errorF) {
    let formData = new FormData();
    // files
    formData.append('media', file, file.name);
    // additional data
    formData.append('name', userDocumentation.name);
    formData.append('start', userDocumentation.start);
    formData.append('end', userDocumentation.end);
    formData.append('role', userDocumentation.roleId);

    WrapperService.post(API_URL + '/profile/user', formData, serviceName, successF, errorF);
  }

  static async readPageableFacilityDocumentationsFromAPI(facilityId, page, size, search, filter, docuTypeCat, sort, sortDesc, successF, errorF) {
    // since default-sorting type/translatedType is not unique and may spread over multiple pages
    // this may cause one document appear on multiple pages - and other not appear on any
    // therefore inserted an additional sort field createdAt desc, so the newest items show on first within any type
    let sortPlusCreatedAt = [...sort, 'createdAt'];
    let sortPlusCreatedAtDesc = [...sortDesc, true];
    let params = createPageableParams(page, size, search, sortPlusCreatedAt, sortPlusCreatedAtDesc, sortFacilityDocumentationFields, sortFacilityDocumentationDatabaseFields);
    params.append('filterBy', filter)
    params.append('docuTypeCategory', docuTypeCat )
    await WrapperService.get(API_URL + '/facility/' + facilityId, {params: params}, 'Documentation-Service: ', successF, errorF);
  }

  static async readFacilityDocumentationScoreForFacility(facilityId, successF, errorF) {
    await WrapperService.get(API_URL + '/facility/' + facilityId + '/score', null, serviceName, successF, errorF);
  }

  /**
   * Posts a document as a facility documentation
   *
   * @param facilityId facility id to add document to
   * @param file file of document
   * @param facilityDocumentation facilityDocumentation object
   * @param successF
   * @param errorF
   */
  static postFacilityDocumentationToAPI(facilityId, file, facilityDocumentation, successF, errorF) {
    let formData = new FormData();
    // files
    formData.append('media', file, file.name);
    // additional data
    formData.append('name', facilityDocumentation.name);
    formData.append('start', facilityDocumentation.start);
    formData.append('end', facilityDocumentation.end);
    formData.append('type', facilityDocumentation.type);

    WrapperService.post(API_URL + '/facility/' + facilityId, formData, serviceName, successF, errorF);
  }

  /**
   * Posts a document as a facility documentation
   *
   * @param customerId facility id to add document to
   * @param file file of document
   * @param customerDocumentation facilityDocumentation object
   * @param successF
   * @param errorF
   */
  static postCustomerDocumentationToAPI(customerId, file, customerDocumentation, successF, errorF) {

    let formData = new FormData();

    formData.append('name', customerDocumentation.name);
    // files
    formData.append('media', file, file.name);

    // additional data
    formData.append('start', customerDocumentation.start);
    formData.append('end', customerDocumentation.end);

    WrapperService.post(API_URL + '/customer/' + customerId, formData, serviceName, successF, errorF);
  }

  /**
   *
   * @param customerId
   * @param successF
   * @param errorF
   * @returns {Promise<void>}
   */
  static async getAllCustomerDocumentation(customerId, successF, errorF) {
    await WrapperService.get(API_URL + '/customer/' + customerId, null, serviceName, successF, errorF);
  }

  /**
   *
   * @param customerId
   * @param documentId
   * @param facilityId
   * @returns {Promise<void>}
   */
  static async assignCustomerDocumentation(customerId, documentId, facilityIdList, successF, errorF) {

    let paramList = {}
    paramList.list = facilityIdList;

    await WrapperService.post(API_URL + '/customerAssign/' + customerId + '/' + documentId, paramList, serviceName, successF, errorF);
  }

  /**
   * Does the same as above, but with an progress information
   * @param facilityId facility id to add document to
   * @param file file of document
   * @param facilityDocumentation facilityDocumentation object
   * @param progress progress of the upload
   * @param successF
   * @param errorF
   */
  static postFacilityDocumentationToAPIWithProgress(facilityId, file, facilityDocumentation, progress, successF, errorF) {
    let formData = new FormData();
    // files
    formData.append('media', file, file.name);
    // additional data
    formData.append('name', facilityDocumentation.name);
    formData.append('start', facilityDocumentation.start);
    formData.append('end', facilityDocumentation.end);
    formData.append('type', facilityDocumentation.type);

    WrapperService.monitoringPost(API_URL + '/facility/' + facilityId, formData, serviceName, progress, successF, errorF);
  }

  static delete(id, successF, errorF) {
    WrapperService.delete(API_URL + '/' + id, null, serviceName, successF, errorF);
  }

  static async saveDocumentForFacilities(documentToSave, successF, errorF) {
    let formData = new FormData();
    // files
    formData.append('document', documentToSave.file);
    // additional data
    formData.append('name', documentToSave.name);
    formData.append('facilityIds', documentToSave.facilityIds);
    WrapperService.post(API_URL + '/file-multi-save', formData, serviceName, successF, errorF);

  }
}
